import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import React, { FC, useState, useEffect, useCallback } from "react";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import Link from "@insite/mobius/Link";
import Delete from "@WhiteCap/Icons/Delete";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    AttributeKeyTypeWithLabel,
    getAttributesWithLabelValue,
    getInitialAttributesWithLabel,
    NOT_FOUND_BRICK_IMG,
} from "@WhiteCap/Utils/GetAttribute";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";

export interface BrickCartCardStyles {
    gridContainer?: GridContainerProps;
    cardWrapper?: InjectableCss;
    cardInfoWrapper?: InjectableCss;
    productName?: InjectableCss;
    productAttribute?: InjectableCss;
    imgWrapper?: InjectableCss;
    deleteBtnLink?: InjectableCss;
    deleteWrapper?: InjectableCss;
}

interface OwnProps {
    handleDeleteBrickCard: Function;
    product: ProductModel;
    extendedStyles?: BrickCartCardStyles;
    css?: StyledProp;
}

export const brickCartCardStyles: BrickCartCardStyles = {
    gridContainer: {
        gap: 40,
    },
    cardWrapper: {
        css: css`
            display: flex;
            background: ${WhitecapColors.white};
            box-shadow: 4px 4px 8px 2px rgba(0, 0, 0, 0.16);
            padding: 16px;
            position: relative;
            margin-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: block;
                            margin-bottom: 20px;
                            &:last-child {
                                margin-bottom: 0px;
                            }
                        `,
                        css`
                            display: block;
                            margin-bottom: 20px;
                            &:last-child {
                                margin-bottom: 0px;
                            }
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
   
    productName: {
        css: css`
            font-size: 18px;
            line-height: 26px;
            margin-bottom: 16px;
            font-style: normal;
            font-weight: 500;
            strong {
                font-weight: 400;
            }
        `,
    },
    productAttribute: {
        css: css`
            div {
                margin-bottom: 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-bottom: 0;
                            `,
                            css`
                                margin-bottom: 0;
                            `,
                            null,
                        ],
                        "max",
                    )}
                label {
                    font-weight: 700;
                    font-size: 16px;
                    line-height: 26px;
                }
                span {
                    font-weight: 400;
                    font-size: 16px;
                    line-height: 26px;
                    margin-left: 3px;
                }
            }
        `,
    },
    imgWrapper: {
        css: css`
            margin-right: 16px;
        `,
    },
    deleteBtnLink: {
        css: css`
            width: 20px;
            height: 20px;
        `,
    },
    deleteWrapper: {
        css: css`
            position: absolute;
            bottom: 10px;
            right: 16px;
        `,
    },
};

type Props = OwnProps;

const BrickCard: FC<Props> = ({ product, extendedStyles, handleDeleteBrickCard }) => {
    const [attributes, setAttributes] = useState<AttributeKeyTypeWithLabel>(getInitialAttributesWithLabel());
    const [styles] = useState(() => mergeToNew(brickCartCardStyles, extendedStyles));

    useEffect(() => {
        const attr = getAttributesWithLabelValue(product);
        setAttributes(attr);
    }, []);

    return (
        <StyledWrapper {...styles.cardWrapper}>
            <StyledWrapper {...styles.imgWrapper}>
                <Img
                    width={180}
                    height={180}
                    src={product.mediumImagePath ? product.mediumImagePath : NOT_FOUND_BRICK_IMG}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.cardInfoWrapper}>
                <StyledWrapper {...styles.productName}>
                    <Typography as="strong">{product.productTitle}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.productAttribute}>
                    {attributes && attributes.color && attributes.color.value && (
                        <StyledWrapper>
                            <Typography as="label" transform="capitalize">
                                {attributes.color.label}:
                            </Typography>
                            <Typography as="span">{attributes.color.value}</Typography>
                        </StyledWrapper>
                    )}
                    {attributes && attributes.style && attributes.style.value && (
                        <StyledWrapper>
                            <Typography as="label" transform="capitalize">
                                {attributes.style.label}:
                            </Typography>
                            <Typography as="span">{attributes.style.value}</Typography>
                        </StyledWrapper>
                    )}
                    {attributes && attributes.manufacturer && attributes.manufacturer.value && (
                        <StyledWrapper>
                            <Typography as="label" transform="capitalize">
                                {attributes.manufacturer.label}:
                            </Typography>
                            <Typography as="span">{attributes.manufacturer.value}</Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.deleteWrapper}>
                <Link title="Remove item" onClick={() => handleDeleteBrickCard(product.id)} {...styles.deleteBtnLink}>
                    <Delete />
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default BrickCard;
